import {
  Container,
  Title,
  Text,
  Card,
  SimpleGrid,
  Group,
  Badge,
  Stack,
  Loader,
  Center,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconCalendar } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import {
  usePendingHealthCertificates,
  usePendingUsers,
  useVolunteers,
} from '../../hooks/useVolunteer';
import { useMyUpcomingShifts } from '../../hooks/useShift';

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const hasAdminAccess =
    user?.role === 'ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.permissions?.includes('admin:manage-users') ||
    user?.permissions?.includes('admin:manage-volunteers');
  const { data: pendingUsers } = usePendingUsers(hasAdminAccess);
  const { data: pendingHealthCertificates } =
    usePendingHealthCertificates(hasAdminAccess);
  const { data: volunteers } = useVolunteers(hasAdminAccess);
  const { data: upcomingShifts, isLoading: shiftsLoading } =
    useMyUpcomingShifts(user?.sub);

  const pendingCount =
    (pendingUsers?.length || 0) + (pendingHealthCertificates?.length || 0);
  const volunteerCount = volunteers?.length || 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1} mb="xs">
            {t('dashboard.title')}
          </Title>
          <Text c="dimmed" size="lg">
            {t('dashboard.welcome', { name: user?.name || user?.email })}
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          <Card
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            component={Link}
            to="/app/calendar"
            style={{
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            <Group justify="space-between" mb="xs">
              <Text fw={500}>{t('dashboard.cards.calendar.title')}</Text>
              <Badge color="teal" variant="light">
                Neu
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {t('dashboard.cards.calendar.description')}
            </Text>
          </Card>

          {hasAdminAccess && (
            <>
              <Card
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                component={Link}
                to="/app/admin/volunteers"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                }}
              >
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>{t('dashboard.cards.volunteers.title')}</Text>
                  <Badge color="green" variant="light">
                    {volunteerCount}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">
                  {t('dashboard.cards.volunteers.description')}
                </Text>
              </Card>
              <Card
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                component={Link}
                to="/app/admin/pending-users"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                }}
              >
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>{t('dashboard.cards.pending.title')}</Text>
                  <Badge color="yellow" variant="light">
                    {pendingCount}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">
                  {t('dashboard.cards.pending.description')}
                </Text>
              </Card>
            </>
          )}
        </SimpleGrid>

        {/* Your Next Shifts Section */}
        <div>
          <Group justify="space-between" mb="md">
            <div>
              <Title order={2} mb="xs">
                {t('dashboard.shifts.title')}
              </Title>
              <Text c="dimmed" size="sm">
                {t('dashboard.shifts.subtitle')}
              </Text>
            </div>
            <Link to="/app/calendar" style={{ textDecoration: 'none' }}>
              <Badge color="blue" variant="light">
                {t('dashboard.shifts.viewAll')}
              </Badge>
            </Link>
          </Group>

          {shiftsLoading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : upcomingShifts && upcomingShifts.length > 0 ? (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
              {upcomingShifts.map(shift => (
                <Card
                  key={shift.slotId}
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                >
                  <Group justify="space-between" mb="md">
                    <Group gap="xs">
                      <IconCalendar size={20} color="teal" />
                      <div>
                        <Text fw={500}>{formatDate(shift.slot.date)}</Text>
                        <Text size="sm" c="dimmed">
                          {shift.slot.area.name}
                        </Text>
                      </div>
                    </Group>
                    <Badge
                      color={shift.status === 'ATTENDS' ? 'green' : 'yellow'}
                      variant="light"
                    >
                      {shift.status === 'ATTENDS'
                        ? 'Eingetragen'
                        : 'Unterstützung'}
                    </Badge>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          ) : (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text c="dimmed" ta="center">
                {t('dashboard.shifts.noShifts')}
              </Text>
              <Group justify="center" mt="md">
                <Link to="/app/calendar" style={{ textDecoration: 'none' }}>
                  <Badge
                    color="blue"
                    component="div"
                    style={{ cursor: 'pointer' }}
                  >
                    {t('dashboard.shifts.registerNow')}
                  </Badge>
                </Link>
              </Group>
            </Card>
          )}
        </div>
      </Stack>
    </Container>
  );
}
